<template>
  <z-dialog
    v-model="visible"
    :title="isEdit ? '编辑数据源' : '创建数据源'"
    width="920px"
    destroy-on-close
    :class="ns.b()"
    @closed="handleClosed"
  >
    <z-steps :active="step" finish-status="success" :class="ns.e('steps')">
      <z-step title="选择系统" />
      <z-step title="配置 SQL" />
      <z-step title="字段配置" />
    </z-steps>

    <div :class="ns.e('body')">
      <div v-show="step === 0" :class="[ns.e('step'), ns.em('step', 'pick')]">
        <aside :class="ns.e('aside')">
          <z-input
            v-model="systemKeyword"
            clearable
            placeholder="搜索系统"
            :class="ns.e('search')"
          />
          <z-collapse v-model="activeSystemGroups" :class="ns.e('collapse')">
            <z-collapse-item title="业务系统" name="biz">
              <ul :class="ns.e('system-list')">
                <li
                  v-for="item in filteredSystems"
                  :key="item.value"
                  :class="[
                    ns.e('system-item'),
                    ns.is('active', form.system === item.value),
                  ]"
                  @click="form.system = item.value"
                >
                  <span :class="ns.e('system-label')">{{ item.label }}</span>
                  <span :class="ns.e('system-code')">{{ item.value }}</span>
                  <p v-if="item.description" :class="ns.e('system-desc')">
                    {{ item.description }}
                  </p>
                </li>
              </ul>
            </z-collapse-item>
          </z-collapse>
        </aside>

        <section :class="ns.e('intro')">
          <h4>只读查询数据源</h4>
          <p>
            数据源通过 <strong>SELECT SQL</strong> 定义，仅支持查询，不可写入。
            保存后可通过「校验」批量检测 SQL 是否可执行。
          </p>
          <dl :class="ns.e('meta')">
            <div>
              <dt>当前系统</dt>
              <dd>{{ selectedSystem?.label ?? '—' }}</dd>
            </div>
            <div>
              <dt>访问路径</dt>
              <dd>
                <code
                  >/{{ form.system || 'system' }}/{{
                    form.dataSourceFlag || 'flag'
                  }}</code
                >
              </dd>
            </div>
            <div>
              <dt>获取 SQL</dt>
              <dd>GET /system/table-config/resolve?path=/mes/custom</dd>
            </div>
            <div>
              <dt>查询数据</dt>
              <dd>POST /system/table-config/query（path: /mes/custom）</dd>
            </div>
          </dl>
        </section>
      </div>

      <div v-show="step === 1" :class="[ns.e('step'), ns.em('step', 'form')]">
        <z-form :model="form" label-width="108px" :class="ns.e('form')">
          <z-form-item label="系统">
            <z-tag>{{ selectedSystem?.label ?? form.system }}</z-tag>
            <span :class="ns.e('readonly-hint')">只读 · 查询</span>
          </z-form-item>
          <z-form-item label="数据源标识" required>
            <z-input
              v-model="form.dataSourceFlag"
              maxlength="64"
              placeholder="如 order_list，与系统联合唯一"
              :disabled="isEdit"
            />
          </z-form-item>
          <z-form-item label="数据源名称" required>
            <z-input
              v-model="form.name"
              maxlength="50"
              placeholder="请输入名称"
            />
          </z-form-item>
          <z-form-item label="描述">
            <z-input
              v-model="form.description"
              type="textarea"
              :rows="2"
              maxlength="50"
              show-word-limit
              placeholder="请输入"
            />
          </z-form-item>
          <z-form-item label="SQL" required :class="ns.e('sql-item')">
            <z-input
              v-model="form.sql"
              type="textarea"
              :rows="8"
              placeholder="SELECT col AS field FROM table WHERE ..."
              :class="ns.e('sql')"
            />
            <div :class="ns.e('sql-actions')">
              <z-button
                link
                type="primary"
                :loading="parsing"
                @click="handleParseSql"
              >
                解析字段
              </z-button>
              <span v-if="parseHint" :class="ns.e('parse-hint')">{{
                parseHint
              }}</span>
            </div>
          </z-form-item>
        </z-form>
      </div>

      <div v-show="step === 2" :class="ns.e('step')">
        <p :class="ns.e('field-tip')">
          以下字段由 SQL
          自动解析，可配置展示名、类型及是否启用（供规则条件选用）。
        </p>
        <z-empty
          v-if="!form.fields.length"
          description="请返回上一步编写 SQL 并解析字段"
        />
        <div v-else :class="ns.e('field-table-wrap')">
          <table :class="ns.e('field-table')">
            <thead>
              <tr>
                <th width="48">启用</th>
                <th>字段 key</th>
                <th>展示名</th>
                <th width="120">类型</th>
                <th>SQL 表达式</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in form.fields" :key="row.field">
                <td>
                  <z-checkbox v-model="row.enabled" />
                </td>
                <td>
                  <code>{{ row.field }}</code>
                </td>
                <td>
                  <z-input v-model="row.label" size="small" />
                </td>
                <td>
                  <z-select v-model="row.type" size="small">
                    <z-option label="文本" value="input" />
                    <z-option label="数字" value="number" />
                    <z-option label="日期" value="date" />
                    <z-option label="开关" value="switch" />
                    <z-option label="LOV" value="lov" />
                  </z-select>
                </td>
                <td :class="ns.e('expr')">{{ row.rawExpr }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #footer>
      <z-button @click="visible = false">取消</z-button>
      <z-button v-if="step > 0" @click="step -= 1">上一步</z-button>
      <z-button
        v-if="step < 2"
        type="primary"
        :disabled="!canNext"
        @click="handleNext"
      >
        下一步
      </z-button>
      <z-button v-if="step >= 1" :loading="validating" @click="handleValidate">
        校验
      </z-button>
      <z-button
        v-if="step === 2"
        type="primary"
        :loading="saving"
        :disabled="!form.fields.some((f) => f.enabled)"
        @click="handleSave"
      >
        保存
      </z-button>
    </template>
  </z-dialog>
</template>

<script setup lang="ts">
import { computed, inject, reactive, ref, watch } from 'vue'
import ZButton from '@zoryn/components/button'
import ZCheckbox from '@zoryn/components/checkbox'
import ZCollapse, { ZCollapseItem } from '@zoryn/components/collapse'
import ZDialog from '@zoryn/components/dialog'
import ZEmpty from '@zoryn/components/empty'
import ZForm, { ZFormItem } from '@zoryn/components/form'
import ZInput from '@zoryn/components/input'
import { GRID_HTTP_INJECTION_KEY } from '@zoryn/components/grid'
import { ZMessage } from '@zoryn/components/message'
import ZSelect, { ZOption } from '@zoryn/components/select'
import { ZStep, ZSteps } from '@zoryn/components/steps'
import ZTag from '@zoryn/components/tag'
import { useNamespace } from '@zoryn/hooks'
import type { HttpRequest } from '@zoryn/network'
import { SYSTEM_OPTIONS } from './constants'
import {
  parseSqlFields,
  saveTableConfigWithFields,
  validateTableConfig,
} from './data-source-api'
import type { SqlDataSource, SqlDataSourceField, SystemOption } from './types'

const props = defineProps<{
  modelValue: boolean
  editing?: SqlDataSource | null
  systemOptions?: SystemOption[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()

const ns = useNamespace('data-source-create-dialog')
const request = inject<HttpRequest | null>(GRID_HTTP_INJECTION_KEY, null)

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const isEdit = computed(() => Boolean(props.editing?.id))
const step = ref(0)
const validating = ref(false)
const parsing = ref(false)
const saving = ref(false)
const parseHint = ref('')
const systemKeyword = ref('')
const activeSystemGroups = ref(['biz'])

const form = reactive({
  id: '',
  system: 'mes',
  dataSourceFlag: '',
  name: '',
  description: '',
  sql: '',
  fields: [] as SqlDataSourceField[],
})

const systemList = computed(() => props.systemOptions ?? SYSTEM_OPTIONS)

const filteredSystems = computed(() => {
  const keyword = systemKeyword.value.trim().toLowerCase()
  if (!keyword) return systemList.value
  return systemList.value.filter(
    (item) =>
      item.label.toLowerCase().includes(keyword) ||
      item.value.toLowerCase().includes(keyword)
  )
})

const selectedSystem = computed(() =>
  systemList.value.find((item) => item.value === form.system)
)

const canNext = computed(() => {
  if (step.value === 0) return Boolean(form.system)
  if (step.value === 1) {
    return Boolean(
      form.dataSourceFlag.trim() && form.name.trim() && form.sql.trim()
    )
  }
  return true
})

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    step.value = 0
    parseHint.value = ''
    if (props.editing) {
      Object.assign(form, {
        id: props.editing.id,
        system: props.editing.system,
        dataSourceFlag: props.editing.dataSourceFlag,
        name: props.editing.name,
        description: props.editing.description ?? '',
        sql: props.editing.sql,
        fields: props.editing.fields.map((item) => ({ ...item })),
      })
    } else {
      Object.assign(form, {
        id: '',
        system: 'mes',
        dataSourceFlag: '',
        name: '',
        description: '',
        sql: '',
        fields: [],
      })
    }
  }
)

async function handleParseSql() {
  if (!request) {
    ZMessage.error('[ZDataSource] 未配置 HttpRequest')
    return
  }
  parsing.value = true
  parseHint.value = ''
  try {
    form.fields = await parseSqlFields(request, form.sql)
    if (!form.fields.length) {
      parseHint.value = '未能解析字段，请使用 SELECT col AS alias FROM ... 格式'
      ZMessage.warning(parseHint.value)
      return
    }
    parseHint.value = `已解析 ${form.fields.length} 个字段`
  } catch (err) {
    ZMessage.error(err instanceof Error ? err.message : '解析失败')
  } finally {
    parsing.value = false
  }
}

async function handleValidate() {
  if (!request) {
    ZMessage.error('[ZDataSource] 未配置 HttpRequest')
    return
  }
  validating.value = true
  try {
    const id =
      form.id && !form.id.startsWith('ds-') ? Number(form.id) : undefined
    const result = await validateTableConfig(request, {
      id: Number.isFinite(id) ? id : undefined,
      customSql: form.sql,
      name: form.name,
    })
    if (result.ok) {
      ZMessage.success(result.message)
      try {
        form.fields = await parseSqlFields(request, form.sql)
        parseHint.value = result.message
      } catch {
        // keep current fields
      }
    } else {
      ZMessage.error(result.message)
    }
  } catch (err) {
    ZMessage.error(err instanceof Error ? err.message : '校验失败')
  } finally {
    validating.value = false
  }
}

async function handleNext() {
  if (step.value === 1 && !form.fields.length) {
    await handleParseSql()
  }
  if (step.value < 2) {
    step.value += 1
  }
}

async function handleSave() {
  if (!request) {
    ZMessage.error('[ZDataSource] 未配置 HttpRequest')
    return
  }
  saving.value = true
  try {
    const payload: SqlDataSource = {
      id: form.id,
      system: form.system,
      dataSourceFlag: form.dataSourceFlag.trim(),
      name: form.name.trim(),
      sql: form.sql.trim(),
      description: form.description.trim() || undefined,
      fields: form.fields.map((item) => ({ ...item })),
    }
    await saveTableConfigWithFields(request, payload)
    emit('saved')
    visible.value = false
    ZMessage.success(isEdit.value ? '数据源已更新' : '数据源已创建')
  } catch (err) {
    ZMessage.error(err instanceof Error ? err.message : '保存失败')
  } finally {
    saving.value = false
  }
}

function handleClosed() {
  step.value = 0
  parseHint.value = ''
}
</script>
